import {
  ExecutionStreamEventType,
  ExecutionTraceState,
  ExecutionStreamChunk,
} from '@shared/orchestration';
import {
  ExecutionTraceStore,
} from './execution-trace-store';

type EventSourceCtor = new (
  url: string,
  eventSourceInitDict?: EventSourceInit,
) => EventSource;

export type TraceStreamStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'error'
  | 'closed';

export interface TraceStreamError {
  type: 'connection' | 'parse' | 'unsupported';
  message: string;
  cause?: unknown;
}

export interface ExecutionTraceStreamOptions {
  sessionId: number;
  messageId: number;
  basePath?: string;
  params?: Record<string, string>;
  withCredentials?: boolean;
  retry?: {
    initial: number;
    multiplier: number;
    max: number;
  };
  useLastEventId?: boolean;
  eventSourceImpl?: EventSourceCtor;
}

export interface ExecutionTraceStreamClientConfig {
  store: ExecutionTraceStore;
  options: ExecutionTraceStreamOptions;
  onStateChange?: (state: ExecutionTraceState, status: TraceStreamStatus) => void;
  onError?: (error: TraceStreamError) => void;
}

const STREAM_EVENT_TYPES: ExecutionStreamEventType[] = [
  'plan',
  'step-start',
  'step-end',
  'tool-result',
  'synthesis-chunk',
  'error',
  'done',
];

export class ExecutionTraceStreamClient {
  private readonly store: ExecutionTraceStore;
  private readonly options: ExecutionTraceStreamOptions;
  private readonly emitState?: (state: ExecutionTraceState, status: TraceStreamStatus) => void;
  private readonly emitError?: (error: TraceStreamError) => void;
  private eventSource: EventSource | null = null;
  private status: TraceStreamStatus = 'idle';
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private retryDelay: number;
  private readonly EventSourceImpl?: EventSourceCtor;
  private eventCounter = 0;

  constructor(config: ExecutionTraceStreamClientConfig) {
    this.store = config.store;
    this.options = config.options;
    this.emitState = config.onStateChange;
    this.emitError = config.onError;
    this.retryDelay = this.options.retry?.initial ?? 1000;
    this.EventSourceImpl = this.options.eventSourceImpl;
  }

  connect() {
    if (this.disposed) return;
    this.clearReconnectTimer();
    this.closeSource();
    this.eventCounter = 0;

    const EventSourceRef =
      this.EventSourceImpl ??
      (typeof window !== 'undefined' ? window.EventSource : undefined);

    if (!EventSourceRef) {
      this.handleError({
        type: 'unsupported',
        message: 'EventSource is not available in this environment.',
      });
      return;
    }

    this.setStatus('connecting');
    const url = this.buildUrl();
    try {
      const source = new EventSourceRef(url, {
        withCredentials: this.options.withCredentials ?? true,
      });
      this.eventSource = source;
      source.addEventListener('open', () => {
        this.retryDelay = this.options.retry?.initial ?? 1000;
        this.setStatus('open');
      });

      for (const eventType of STREAM_EVENT_TYPES) {
        source.addEventListener(eventType, (event) => {
          this.handleMessage(eventType, event as MessageEvent);
        });
      }

      source.onerror = (event) => {
        this.handleError({
          type: 'connection',
          message: 'Execution trace stream encountered an error.',
          cause: event,
        });
      };
    } catch (error) {
      this.handleError({
        type: 'connection',
        message: 'Unable to establish execution trace stream.',
        cause: error,
      });
    }
  }

  reconnect(forceReset = false) {
    if (forceReset) {
      this.retryDelay = this.options.retry?.initial ?? 1000;
      this.store.reset();
      this.emitState?.(this.store.getState(), 'connecting');
    }
    this.connect();
  }

  dispose() {
    this.disposed = true;
    this.clearReconnectTimer();
    this.closeSource();
    this.setStatus('closed');
  }

  private buildUrl(): string {
    const base =
      this.options.basePath ?? '/api/smart-chat/v2/orchestrate/stream';
    const params = new URLSearchParams({
      sessionId: String(this.options.sessionId),
    });
    for (const [key, value] of Object.entries(this.options.params ?? {})) {
      if (value !== undefined && value !== null) {
        params.set(key, value);
      }
    }
    if (this.options.useLastEventId) {
      const lastId = this.store.getLastEventId();
      if (lastId) {
        params.set('lastEventId', lastId);
      }
    }
    return `${base}?${params.toString()}`;
  }

  private handleMessage(type: ExecutionStreamEventType, event: MessageEvent) {
    try {
      const { envelope, payload } = this.parseEventEnvelope(event);
      const chunk: ExecutionStreamChunk = {
        id: this.resolveChunkId(envelope, event, type),
        type,
        data: (payload ?? {}) as ExecutionStreamChunk['data'],
        timestamp: this.resolveChunkTimestamp(envelope),
        version: (typeof envelope.version === 'string'
          ? (envelope.version as ExecutionStreamChunk['version'])
          : '2.0') ?? '2.0',
      };

      const changed = this.store.applyChunk(chunk);
      if (changed) {
        this.emitState?.(this.store.getState(), this.status);
      }
      if (type === 'done') {
        this.setStatus('closed');
        this.closeSource();
      }
    } catch (error) {
      this.handleError({
        type: 'parse',
        message: 'Failed to parse execution trace event payload.',
        cause: error,
      });
    }
  }

  private parseEventEnvelope(event: MessageEvent): {
    envelope: Record<string, unknown>;
    payload: unknown;
  } {
    const raw = event.data;
    const envelope = this.parseJsonRecord(raw);
    if (
      envelope &&
      Object.prototype.hasOwnProperty.call(envelope, 'data')
    ) {
      const inner = (envelope as Record<string, unknown>).data;
      const parsedInner =
        typeof inner === 'string' ? this.parseJson(inner) : inner;
      return {
        envelope,
        payload: parsedInner ?? {},
      };
    }
    return {
      envelope,
      payload: envelope,
    };
  }

  private parseJsonRecord(value: unknown): Record<string, unknown> {
    if (typeof value === 'string') {
      const parsed = this.parseJson(value);
      return this.isPlainObject(parsed) ? parsed : {};
    }
    if (this.isPlainObject(value)) {
      return value;
    }
    return {};
  }

  private parseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private resolveChunkId(
    envelope: Record<string, unknown>,
    event: MessageEvent,
    type: ExecutionStreamEventType,
  ): string {
    const lastEventId =
      typeof event.lastEventId === 'string' && event.lastEventId.length > 0
        ? event.lastEventId
        : undefined;
    const envelopeId =
      typeof envelope.id === 'string' && envelope.id.length > 0
        ? envelope.id
        : undefined;
    if (lastEventId) {
      return lastEventId;
    }
    if (envelopeId) {
      return envelopeId;
    }
    return `${type}-${Date.now()}-${this.eventCounter++}`;
  }

  private resolveChunkTimestamp(envelope: Record<string, unknown>): number {
    if (typeof envelope.timestamp === 'number') {
      return envelope.timestamp;
    }
    return Date.now();
  }

  private handleError(error: TraceStreamError) {
    if (this.disposed) return;
    this.emitError?.(error);
    this.setStatus('error');
    this.closeSource();
    this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (this.disposed) return;
    this.clearReconnectTimer();
    const delay = this.retryDelay;
    this.reconnectTimer = setTimeout(() => {
      this.retryDelay = Math.min(
        (this.retryDelay || 1000) * (this.options.retry?.multiplier ?? 2),
        this.options.retry?.max ?? 30_000,
      );
      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeSource() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private setStatus(status: TraceStreamStatus) {
    this.status = status;
    this.emitState?.(this.store.getState(), status);
  }
}
